import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  DISCLAIMER,
  dayBullets,
  headline,
  mechanicsNote,
} from "@aucash/rules/second-earner";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import {
  Checkbox,
  Field,
  MoneyInput,
  Segmented,
  Stepper,
} from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// second-earner — "what does an extra work day actually add?", surfaced
// stateless-v1 (PLAN arch decision #1). One screen: the result renders INLINE
// (no save-by-id, no auth gate, no persistence). Each compute is a stateless
// `calc.secondEarner.compute` query (vanilla tRPC client `.query()` in the
// submit handler, result held in useState — no useQuery in this codebase).
//
// The module composes the au13 CCS step-down + the income-tax marginal rate
// (resolved in the rules layer). All result copy comes verbatim from the rules
// package's copy helpers, so the wording can never drift from the API/SEO
// surfaces (validation §3: information, not advice). HARD RULE: this surface
// presents GROSS beside NET as NEUTRAL MECHANICS — the per-day waterfall of what
// tax + FTB-A withdrawal + the CCS step-down strip — and NEVER a "worth it /
// work more / stay home" verdict. The rules-layer `assertNoVerdict` guard
// already enforces that on the output we render. No financial-product handoff.

const ASSESSMENT_YEAR = "2025-26";

type CareType = "centre_based_day_care" | "family_day_care";

const CARE_OPTIONS: ReadonlyArray<{ value: CareType; label: string }> = [
  { value: "centre_based_day_care", label: "Centre-based" },
  { value: "family_day_care", label: "Family day care" },
];

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.secondEarner.compute.query>
>;

export default function SecondEarnerIndex(): JSX.Element {
  const router = useRouter();

  const [primaryIncome, setPrimaryIncome] = useState("");
  const [secondEarnerBaseIncome, setSecondEarnerBaseIncome] = useState("");
  const [dayGross, setDayGross] = useState("");
  const [daysToModel, setDaysToModel] = useState(5);
  const [hasStudyLoan, setHasStudyLoan] = useState(false);

  // Optional: one child in subsidised care (the CCS step-down driver).
  const [hasChildInCare, setHasChildInCare] = useState(false);
  const [careType, setCareType] = useState<CareType>("centre_based_day_care");
  const [hoursUsed, setHoursUsed] = useState("");
  const [hourlyFee, setHourlyFee] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const buildPayload = (): {
    input: Parameters<typeof trpc.calc.secondEarner.compute.query>[0]["input"];
  } => ({
    input: {
      primaryIncome: num(primaryIncome),
      secondEarnerBaseIncome: num(secondEarnerBaseIncome),
      dayGross: num(dayGross),
      daysToModel,
      ftbAEntitlement: { maxRateAnnual: 0, baseRateAnnual: 0 },
      children: hasChildInCare
        ? [
            {
              hoursUsed: num(hoursUsed),
              careType,
              hourlyFee: num(hourlyFee),
              activityHours: 48,
              schoolAged: false,
              indigenousChild: false,
              childRank: 1,
            },
          ]
        : [],
      hasStudyLoan,
      assessmentYear: ASSESSMENT_YEAR,
    },
  });

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (primaryIncome.trim() === "")
      next.primary = "Enter your partner's annual income.";
    if (dayGross.trim() === "")
      next.dayGross = "Enter the gross pay for one extra day.";
    if (hasChildInCare) {
      if (hoursUsed.trim() === "")
        next.hours = "Enter the care hours per fortnight.";
      if (hourlyFee.trim() === "")
        next.fee = "Enter the hourly fee the service charges.";
    }
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.secondEarner.compute
      .query(buildPayload())
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Second earner" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · extra work day</Eyebrow>
            <Text style={styles.title}>What does an extra work day add?</Text>
            <Text style={styles.subtitle}>
              For a second earner in a couple family, an extra day of work isn't
              the gross day-rate: it's taxed at your marginal rate, lifts combined
              family income (withdrawing Family Tax Benefit Part A), and steps your
              Child Care Subsidy down so the same care costs more. See the per-day
              gross beside the net, with each reduction cited to its source.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Your partner's annual income"
            help="The other earner in the couple — their income is fixed across the scenario."
            error={errors.primary}
          >
            <MoneyInput
              value={primaryIncome}
              onChange={setPrimaryIncome}
              testID="input-primary-income"
              accessibilityLabel="Your partner's annual income"
            />
          </Field>

          <Field
            label="Your income before the extra days"
            help="What you already earn — the modelled days stack on top of this."
          >
            <MoneyInput
              value={secondEarnerBaseIncome}
              onChange={setSecondEarnerBaseIncome}
              testID="input-base-income"
              accessibilityLabel="Your income before the extra days"
            />
          </Field>

          <Field
            label="Gross pay for one extra day"
            help="Your gross pay for a single additional day of work."
            error={errors.dayGross}
          >
            <MoneyInput
              value={dayGross}
              onChange={setDayGross}
              testID="input-day-gross"
              accessibilityLabel="Gross pay for one extra day"
            />
          </Field>

          <Field label="How many extra days to model?">
            <Stepper
              value={daysToModel}
              onChange={setDaysToModel}
              min={1}
              max={5}
              suffix={daysToModel === 1 ? "day" : "days"}
              testID="input-days"
              accessibilityLabel="Extra days to model"
            />
          </Field>

          <Field label="Do you have a child in subsidised care?">
            <Checkbox
              checked={hasChildInCare}
              onChange={setHasChildInCare}
              label="Yes — include the Child Care Subsidy step-down for one child."
            />
          </Field>

          {hasChildInCare ? (
            <>
              <Field label="Care type">
                <Segmented<CareType>
                  options={CARE_OPTIONS}
                  value={careType}
                  onChange={setCareType}
                  testIDPrefix="care-type"
                />
              </Field>
              <Field
                label="Care hours per fortnight"
                help="Subsidised hours of care used per fortnight for this child."
                error={errors.hours}
              >
                <MoneyInput
                  value={hoursUsed}
                  onChange={setHoursUsed}
                  testID="input-hours"
                  accessibilityLabel="Care hours per fortnight"
                />
              </Field>
              <Field
                label="Hourly fee the service charges"
                error={errors.fee}
              >
                <MoneyInput
                  value={hourlyFee}
                  onChange={setHourlyFee}
                  testID="input-hourly-fee"
                  accessibilityLabel="Hourly fee the service charges"
                />
              </Field>
            </>
          ) : null}

          <Field label="Do you have a HELP / study loan?">
            <Checkbox
              checked={hasStudyLoan}
              onChange={setHasStudyLoan}
              label="Yes — an extra day can also lift my compulsory study-loan repayment."
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="second-earner-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any
 * number (validation §3.2 placement rule). Copy comes verbatim from the rules
 * package (headline / dayBullets / mechanicsNote): neutral mechanics, gross
 * beside net, NO verdict. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const out = result.output;
  const bullets = dayBullets(out);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline(out)}</Text>
      </Card>

      <Section title="Each extra day, broken down">
        {bullets.map((b, i) => (
          <Text key={`day-${i}`} style={styles.bodyText}>
            {b}
          </Text>
        ))}
        <Text style={styles.bodyText}>{mechanicsNote()}</Text>
      </Section>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          Figures use the {result.ruleVersion} ruleset, last verified{" "}
          {result.sourcesVerifiedAt}. Income tax + Medicare levy via the ATO
          schedules; Family Tax Benefit Part A and the Child Care Subsidy
          step-down via the Services Australia income tests.
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    lineHeight: 29,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
